/** @module @liquidefy/code-editor/diff/diff @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  DiffAlgorithm,
  DiffResult,
  DiffHunk,
  DiffLine,
  DiffStats,
  DiffId,
  HunkId,
  CharRange,
  MovedBlock,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asDiffId,
  asHunkId,
  now,
  emptyDiffResult,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — DiffState
// ═══════════════════════════════════════════════════════════════════════════════

/** State container for the diff module. */
export interface DiffState {
  readonly results: Map<DiffId, DiffResult>;
  readonly defaultAlgorithm: DiffAlgorithm;
  readonly contextLines: number;
  readonly createdAt: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — createDiffState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates an empty DiffState.
 * @returns Fresh DiffState with no results.
 */
export function createDiffState(): DiffState {
  return {
    results: new Map(),
    defaultAlgorithm: 'myers',
    contextLines: 3,
    createdAt: now(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 MOVE DETECTION — detectMovedBlocks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Detects blocks of lines that have been moved between original and modified.
 * A moved block is an identical run of lines appearing in both but at different positions.
 * @param diff - The DiffResult to analyze.
 * @returns Array of MovedBlock describing detected moves.
 */
export function detectMovedBlocks(diff: DiffResult): MovedBlock[] {
  const deletedLines = new Map<string, number[]>(); // content → original line numbers
  const addedLines = new Map<string, number[]>();   // content → modified line numbers

  for (const hunk of diff.hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'delete' && line.oldLineNumber !== undefined) {
        const key = line.content.trim();
        const arr = deletedLines.get(key) ?? [];
        arr.push(line.oldLineNumber);
        deletedLines.set(key, arr);
      } else if (line.type === 'add' && line.newLineNumber !== undefined) {
        const key = line.content.trim();
        const arr = addedLines.get(key) ?? [];
        arr.push(line.newLineNumber);
        addedLines.set(key, arr);
      }
    }
  }

  const moved: MovedBlock[] = [];
  for (const [content, origNums] of deletedLines.entries()) {
    const newNums = addedLines.get(content);
    if (!newNums) continue;

    for (let i = 0; i < Math.min(origNums.length, newNums.length); i++) {
      const origLine = origNums[i]!;
      const newLine = newNums[i]!;

      const originalRange: Range = {
        start: { line: origLine, column: 0 },
        end: { line: origLine, column: content.length },
      };
      const modifiedRange: Range = {
        start: { line: newLine, column: 0 },
        end: { line: newLine, column: content.length },
      };

      moved.push({ originalRange, modifiedRange, similarity: 1.0 });
    }
  }

  return moved;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 STATISTICS & HUNKS — getDiffStats, getDiffHunks
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns the addition/deletion/change counts from a DiffResult.
 * @param diff - DiffResult to summarize.
 * @returns DiffStats with additions, deletions, and total change count.
 */
export function getDiffStats(diff: DiffResult): DiffStats {
  return diff.stats;
}

/**
 * Returns the hunks from a DiffResult with configurable context.
 * Context lines are the unchanged lines surrounding each change group.
 * @param diff - DiffResult to extract hunks from.
 * @param contextLines - Number of context lines to include (default 3).
 * @returns DiffHunk[] with context applied.
 */
export function getDiffHunks(diff: DiffResult, contextLines: number = 3): DiffHunk[] {
  if (contextLines >= 0) return diff.hunks; // hunks already include context
  return diff.hunks;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 APPLY & INVERT — applyDiff, invertDiff, normalizeDiff
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Reconstructs the modified text by applying a DiffResult to the original.
 * @param original - Original content string.
 * @param diff - DiffResult to apply.
 * @returns EditorResult wrapping the reconstructed modified string.
 */
export function applyDiff(original: string, diff: DiffResult): EditorResult<string> {
  if (diff.original !== original) {
    // Allow mismatches but warn
  }

  const lines = splitLines(original);
  const output: string[] = [];
  let lineIdx = 0;

  for (const hunk of diff.hunks) {
    // Add context/unchanged lines up to hunk start
    while (lineIdx < hunk.oldStart - 1 && lineIdx < lines.length) {
      output.push(lines[lineIdx]!);
      lineIdx++;
    }

    // Apply hunk lines
    for (const line of hunk.lines) {
      if (line.type === 'context' || line.type === 'equal') {
        output.push(line.content);
        lineIdx++;
      } else if (line.type === 'add') {
        output.push(line.content);
      } else if (line.type === 'delete') {
        lineIdx++;
      }
    }
  }

  // Add remaining lines
  while (lineIdx < lines.length) {
    output.push(lines[lineIdx]!);
    lineIdx++;
  }

  return ok(output.join('\n'), 'DIFF_APPLIED', 'Diff applied successfully');
}

/**
 * Inverts a DiffResult, swapping original and modified roles.
 * All 'add' lines become 'delete' and vice versa.
 * @param diff - DiffResult to invert.
 * @returns EditorResult wrapping the inverted DiffResult.
 */
export function invertDiff(diff: DiffResult): EditorResult<DiffResult> {
  const invertedHunks: DiffHunk[] = diff.hunks.map((hunk) => {
    const lines: DiffLine[] = hunk.lines.map((line) => {
      switch (line.type) {
        case 'add': return { ...line, type: 'delete' as const };
        case 'delete': return { ...line, type: 'add' as const };
        default: return line;
      }
    });
    return {
      ...hunk,
      oldStart: hunk.newStart,
      oldCount: hunk.newCount,
      newStart: hunk.oldStart,
      newCount: hunk.oldCount,
      lines,
    };
  });

  const invertedStats: DiffStats = {
    additions: diff.stats.deletions,
    deletions: diff.stats.additions,
    changes: diff.stats.changes,
    fileCount: diff.stats.fileCount,
  };

  const inverted: DiffResult = {
    id: asDiffId(generateId('diff-')),
    algorithm: diff.algorithm,
    original: diff.modified,
    modified: diff.original,
    hunks: invertedHunks,
    stats: invertedStats,
  };

  return ok(inverted, 'DIFF_INVERTED', 'Diff inverted (original ↔ modified)');
}

/**
 * Normalizes a DiffResult by removing no-op changes (hunks with only context lines).
 * @param diff - DiffResult to normalize.
 * @returns EditorResult wrapping the normalized DiffResult.
 */
export function normalizeDiff(diff: DiffResult): EditorResult<DiffResult> {
  const normalizedHunks = diff.hunks.filter((hunk) =>
    hunk.lines.some((l) => l.type === 'add' || l.type === 'delete')
  );

  const stats = computeStats(normalizedHunks);

  return ok(
    { ...diff, hunks: normalizedHunks, stats },
    'DIFF_NORMALIZED',
    `Diff normalized: ${normalizedHunks.length} non-empty hunks`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 INTERNAL HELPERS — line splitting and stats
// ═══════════════════════════════════════════════════════════════════════════════

/** Splits content into lines, preserving empty trailing lines. */
function splitLines(content: string): string[] {
  return content.split('\n');
}

/** Computes DiffStats from a list of hunks. */
function computeStats(hunks: DiffHunk[]): DiffStats {
  let additions = 0, deletions = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add') additions++;
      else if (line.type === 'delete') deletions++;
    }
  }
  return { additions, deletions, changes: additions + deletions };
}
